import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { DEPOSIT_ACTIVATION_STATUS } from "src/consts/deposit";
import { PASSED_VERIF_STATUS } from "src/consts/user";
import { useIsLoadingFeatureToggle } from "src/contexts/FeaturesToggle";
import { isUserOnTrial, isVerificationPassed } from "src/helpers/user";
import { NON_ELIGILBE_BUSINESS_FORM, NON_ELIGILBE_INDUSTRIES } from "src/scenes/Guest/consts/users";
import { KYC_STATUS } from "src/scenes/NewVerification/consts/kyc";
import { isHighRisk, isLowMedRisk, isUMKM } from "src/scenes/NewVerification/utils/verificationUtils";
import { useSelector } from "./state";
import privateRoutes from "src/routes/private-routes";

export function useUserInfo() {
  const {
    user,
    verificationInfo,
    loadingUser,
    loadingVerification,
    errorFetchVerificationInfo,
    isRouteClean,
    verifications,
    loadingVerifications,
    errorFetchVerifications,
    isOwner,
    isDeveloper,
    isReviewer,
    isApprover,
    isChecker,
    isAdmin,
    isExecutor,
    companyInfo,
    loadingCompanyInfo,
    errorFetchCompanyInfo
  } = useSelector((state) => state.commonUser);
  const { loadingTrial, trial } = useSelector((state) => state.commonTrial);
  const isLoadingFeatureToggle = useIsLoadingFeatureToggle();

  return {
    isDoneLoading: user && Object.entries(user).length > 0 && !loadingUser && !isLoadingFeatureToggle,
    isOnTrial: !loadingTrial && !loadingVerification && isUserOnTrial(trial),
    isUserVerified: user?.current_user?.email_verified && user?.current_user?.phone_number_verified,
    isVerificationPassed: isVerificationPassed(user) === PASSED_VERIF_STATUS.PASSED,
    isKYCWaitingApproval: user?.company?.kyc_status === KYC_STATUS.WAITING_APPROVAL,
    isKYCCompanyDataSubmitted: user?.company?.kyc_status === KYC_STATUS.COMPANY_DATA_SUBMITTED,
    isKYCPhoneVerified: user?.company?.kyc_status === KYC_STATUS.PHONE_VERIFIED,
    isKYCDone: user?.company?.kyc_status === KYC_STATUS.DONE,
    isKYCApproved: user?.company?.kyc_status === KYC_STATUS.APPROVED,
    isKYCNegotiate: user?.company?.kyc_status === KYC_STATUS.NEGOTIATE,
    isKYCRequested: user?.company?.kyc_status === KYC_STATUS.REQUESTED,
    isKYCRejected: user?.company?.kyc_status === KYC_STATUS.REJECTED,
    isDepositActive: user?.company?.deposit_status === DEPOSIT_ACTIVATION_STATUS.ACTIVE,
    isDepositInactive: user?.company?.deposit_status === DEPOSIT_ACTIVATION_STATUS.INACTIVE,
    kycStatus: user?.company?.kyc_status,
    companyFormOfBusiness: user?.company?.form_of_business,
    companyIndustry: user?.company?.industry,
    companyName: user?.company?.name_of_business,
    isUMKM: isUMKM(user?.company?.form_of_business, user?.company?.industry),
    isLowMedIndustry:
      ["Low", "Medium"].includes(user?.company?.risk_level) ||
      isLowMedRisk(user?.company?.form_of_business, user?.company?.industry),
    isHighRiskIndustry:
      user?.company?.risk_level === "High" || isHighRisk(user?.company?.form_of_business, user?.company?.industry),
    isNotEligibleTrial:
      NON_ELIGILBE_BUSINESS_FORM.includes(user?.company?.form_of_business) ||
      NON_ELIGILBE_INDUSTRIES.includes(user?.company?.industry),
    isRouteClean,
    user,
    verificationInfo,
    loadingUser,
    loadingVerification,
    errorFetchVerificationInfo,
    verifications,
    loadingVerifications,
    errorFetchVerifications,
    isOwner,
    isDeveloper,
    isReviewer,
    isApprover,
    isChecker,
    isAdmin,
    isExecutor,
    companyInfo,
    loadingCompanyInfo,
    errorFetchCompanyInfo
  };
}

export function useUserSanction() {
  const { user } = useUserInfo();
  const location = useLocation();

  const isRestricted = useMemo(() => {
    // return true if it's MT sanctioned and in MT's routes
    if (user?.is_mt_transaction_restricted && location.pathname === privateRoutes.transaction.path) {
      return true;
    }

    // return true if it's IT sanctioned and in IT's routes
    if (
      user?.is_it_transaction_restricted &&
      location.pathname === privateRoutes.internationalTransaction.path &&
      user?.company?.globe_status
    ) {
      return true;
    }

    // return true if it's AP sanctioned and in AP's routes
    if (user?.is_ap_transaction_restricted && location.pathname === privateRoutes.acceptPayment.path) {
      return true;
    }

    // return true if it's MT or IT sanctioned and in create refund route
    if (
      (user?.is_mt_transaction_restricted || user?.is_it_transaction_restricted) &&
      location.pathname === privateRoutes.createRefund.path
    ) {
      return true;
    }

    return false;
  }, [
    user?.is_mt_transaction_restricted,
    user?.is_it_transaction_restricted,
    user?.is_ap_transaction_restricted,
    location.pathname
  ]);

  return {
    isRestricted
  };
}
